import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import dayjs from "dayjs";
import { RetellClient, WebCallSession } from "retell-client-js-sdk";
import {
  PhoneIcon,
} from "@heroicons/react/24/solid";
import NoSleep from "nosleep.js";
import Vapi from "@vapi-ai/web";

import { Button } from "./ui/components/Button";
import { Avatar } from "./ui/components/Avatar";
import { IconWithBackground } from "./ui/components/IconWithBackground";

const defaultAgentId = process.env.REACT_APP_DEFAULT_AGENT_ID || "";

const FROM_PHONE_NUMBER = process.env.REACT_APP_FROM_PHONE_NUMBER || "";

// The Railway function holds the real API key and forwards the SDK's control
// requests to api.retellai.com, so the key here is never used by Retell.
const registerCallApiUrl = process.env.REACT_APP_REGISTER_CALL_API_URL || "";
const retellClient = new RetellClient({
  key: "proxied-by-backend",
  baseURL: registerCallApiUrl ? new URL(registerCallApiUrl).origin : undefined,
});

const vapi = new Vapi(process.env.REACT_APP_VAPI_API_KEY || "");

const App = () => {
  const [isCalling, setIsCalling] = useState(false);
  const [shownScheduling, setShownScheduling] = useState(false);
  const [isFormCallMode, setIsFormCallMode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [personName, setPersonName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sessionRef = useRef<WebCallSession | null>(null);

  // Initialize the SDK
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const method = searchParams.get("method");
    if (method === "formcall") {
      setIsFormCallMode(true);
    }
    const useVapi = searchParams.get("v");
    if (useVapi === "true") {
      vapi.on("call-start", () => {
        setIsCalling(true);
      });
      vapi.on("call-end", () => {
        setIsCalling(false);
      });
      vapi.on("error", (error) => {
        console.error("An error occurred:", error);
        setIsCalling(false);
      });
    } else {
      navigator.mediaDevices.getUserMedia({ audio: true }).catch(console.error);
    }

    if (searchParams.get("sleep") === "true") {
      document.addEventListener("click", function enableNoSleep() {
        const noSleep = new NoSleep();
        document.removeEventListener("click", enableNoSleep, false);
        noSleep.enable();
      }, false);
    }
  }, []);

  const toggleConversation = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const useVapi = searchParams.get("v");
    let agentId = searchParams.get("agent_id");
    if (!agentId) {
      agentId = defaultAgentId;
    }
    if (useVapi === "true") {
      if (isCalling) {
        vapi.stop();
        if (!shownScheduling) {
          window.open("https://cal.com/darin-loh/havana-demo", "_blank");
          setShownScheduling(true);
        }
      } else {
        vapi.start(agentId);
      }
    } else {
      if (isCalling) {
        sessionRef.current?.end();
        sessionRef.current = null;
        if (!shownScheduling) {
          window.open("https://cal.com/darin-loh/havana-demo", "_blank");
          setShownScheduling(true);
        }
      } else {
        sessionRef.current = retellClient.createWebCall({
          agent_id: agentId,
          retell_llm_dynamic_variables: buildCallVariables(),
          hooks: {
            onEnd: () => {
              setIsCalling(false); // Update button to "Start" when conversation ends
            },
            onError: (error) => {
              console.error("An error occurred:", error);
              setIsCalling(false); // Update button to "Start" in case of error
            },
          },
        });
        setIsCalling(true); // Update button to "Stop" when conversation starts
      }
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber || !personName) {
      alert("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await createPhoneCall(phoneNumber, personName);
      // Reset form after successful call
      setPhoneNumber("");
      setPersonName("");
    } catch (err) {
      // Error already handled in createPhoneCall
    } finally {
      setIsSubmitting(false);
    }
  };

  async function createPhoneCall(toNumber: string, firstName: string): Promise<void> {
    const searchParams = new URLSearchParams(window.location.search);
    let agentId = searchParams.get("agent_id");
    if (!agentId) {
      agentId = defaultAgentId;
    }

    const dynamicVariables: Record<string, string> =
      searchParams.get("override_fallback") === "1"
        ? {}
        : { "lead.first_name": firstName };
    searchParams.forEach((value, key) => {
      if (key.startsWith("fv_")) {
        dynamicVariables[key.slice(3)] = value;
      }
    });

    try {
      const response = await fetch(
        process.env.REACT_APP_PHONE_CALL_API_URL || "",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agent_id: agentId,
            from_number: FROM_PHONE_NUMBER,
            to_number: toNumber,
            retell_llm_dynamic_variables: dynamicVariables,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      console.log("Phone call created:", data);
      alert(`Now calling ${toNumber}, please wait...`);
      if (!shownScheduling) {
        window.open("https://cal.com/darin-loh/havana-demo", "_blank");
        setShownScheduling(true);
      }
    } catch (err) {
      console.error("Error creating phone call:", err);
      alert(`Failed to create call, please try again later`);
      throw err;
    }
  }

  function buildCallVariables(): Record<string, string> {
    let dateHelper = `Today (${dayjs().format("dddd")}): ${dayjs().format(
      "MMM D, YYYY",
    )}\n`;
    dateHelper += `Tomorrow (${dayjs()
      .add(1, "day")
      .format("dddd")}): ${dayjs().add(1, "day").format("MMM D, YYYY")}\n`;
    let checkedDay = dayjs().add(1, "day");
    let sundays = 0;
    for (;;) {
      checkedDay = checkedDay.add(1, "day");
      if (sundays === 0) {
        dateHelper += `${checkedDay.format("dddd")}: ${checkedDay.format(
          "MMM D, YYYY",
        )}\n`;
      } else if (sundays === 1) {
        dateHelper += `Next ${checkedDay.format("dddd")}: ${checkedDay.format(
          "MMM D, YYYY",
        )}\n`;
      } else if (sundays === 2) {
        break;
      }
      if (checkedDay.day() === 0) {
        sundays++;
      }
    }
    dateHelper += `Time: ${dayjs().format("h:mm A")}\n`;

    const times = [
      dayjs().add(1, "day").set("hour", 9).set("minute", 30).set("second", 0).set("millisecond", 0),
      dayjs().add(1, "day").set("hour", 15).set("minute", 30).set("second", 0).set("millisecond", 0),
      dayjs().add(3, "day").set("hour", 9).set("minute", 30).set("second", 0).set("millisecond", 0),
    ];
    const timesForDay = new Map<string, dayjs.Dayjs[]>();
    for (const time of times) {
      const t = dayjs(time);
      const dateKey = t.format("YYYY-MM-DD");
      if (!timesForDay.has(dateKey)) {
        timesForDay.set(dateKey, []);
      }
      timesForDay.get(dateKey)?.push(t);
    }
    const sortedTimes = Array.from(timesForDay.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
    let firstTime = true;
    let appointmentHelper = "";
    for (const [, times] of sortedTimes) {
      // e.g.
      // * 9:30 AM or 5 PM on Monday (Jun 23)
      // * Or 9:30 AM or 5 PM on Tuesday (Jun 24)
      appointmentHelper += `* ${firstTime ? "" : "Or "}${times
        .map((t) => t.format("h:mm A"))
        .join(" or ")} on ${times[0].format("dddd")} (${times[0].format(
        "MMM D",
      )})\n`;
      firstTime = false;
    }
    if (times.length === 0) {
      appointmentHelper = "No appointment slots available.";
    }


    const searchParams = new URLSearchParams(window.location.search);
    const variables: Record<string, string> =
      searchParams.get("override_fallback") === "1"
        ? {}
        : {
            name: "Nathan",
            token: "testtoken123",
            date_helper: dateHelper,
            appointment_helper: appointmentHelper,
            time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
    searchParams.forEach((value, key) => {
      if (key.startsWith("fv_")) {
        variables[key.slice(3)] = value;
      }
    });

    return variables;
  }

  return (
    <div className="flex h-full flex-col p-0">
      <div className="px-6 py-2">
        <img
          className="h-8 flex-none object-cover"
          src="https://res.cloudinary.com/subframe/image/upload/v1744606003/uploads/10128/z9wb1loxmzedoa4gsqd4.png"
          alt="logo"
        />
      </div>
      <div className="flex flex-col items-center transition-all duration-500 ease-in-out px-4 h-full py-2 justify-around">
        <div className="flex flex-col items-center gap-2">
          <Button
            className="mobile:h-6 mobile:w-auto mobile:flex-none"
            variant="brand-primary"
            icon={null}
            iconRight={null}
          >
            VOICE DEMO
          </Button>
          <span className="max-w-[576px] whitespace-pre-wrap font-['Inter'] text-[48px] font-[500] leading-[62px] text-[#02015aff] text-center -tracking-[0.04em] mobile:font-['Inter'] mobile:text-[36px] mobile:font-[500] mobile:leading-[48px] mobile:tracking-normal">
            {"Meet Havana"}
          </span>
          <div className={`flex flex-col items-center transition-all duration-500 ease-in-out ${isCalling ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-96'}`}>
              <span className="whitespace-pre-wrap font-['Inter'] text-[32px] font-[400] leading-[40px] text-[#4e5774ff] text-center -tracking-[0.04em] mobile:font-['Inter'] mobile:text-[20px] mobile:font-[400] mobile:leading-[32px] mobile:tracking-normal">
                {"Experience Our AI Enrollment Advisor"}
              </span>
              <span className="font-['Inter'] text-[20px] font-[400] leading-[28px] text-[#b3b3beff] -tracking-[0.04em] text-center">
                {
                  "Our AI is customizable — we can train it to answer any question in just minutes."
                }
              </span>
          </div>
        </div>
        <div>
          <Avatar
            className={`mobile:h-28 mobile:w-28 mobile:flex-none ${isCalling ? 'animate-pulse shadow-lg shadow-blue-300 ring-2 ring-blue-500 transition-all duration-500' : ''}`}
            size="x-large"
            image="https://res.cloudinary.com/subframe/image/upload/v1744700334/uploads/10128/w2gpfzymjlpnwrs1byjb.png"
            square={false}
          >
            A
          </Avatar>
        </div>
        <div className="flex flex-col items-center gap-4">
          {isFormCallMode ? (
            <form onSubmit={handleFormSubmit} className="flex flex-col gap-4 w-full max-w-md px-4">
              <div className="flex flex-col gap-2">
                <label className="font-['Inter'] text-[16px] font-[500] text-[#02015aff]">
                  Name of student
                </label>
                <input
                  type="text"
                  value={personName}
                  onChange={(e) => setPersonName(e.target.value)}
                  placeholder="Enter first name"
                  className="px-4 py-3 border-2 border-[#e5e7ebff] rounded-lg font-['Inter'] text-[16px] focus:outline-none focus:border-[#214eff] transition-colors"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-['Inter'] text-[16px] font-[500] text-[#02015aff]">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                  className="px-4 py-3 border-2 border-[#e5e7ebff] rounded-lg font-['Inter'] text-[16px] focus:outline-none focus:border-[#214eff] transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-[#214eff] text-white font-['Inter'] text-[18px] font-[500] rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Creating Call..." : "Start Call"}
              </button>
            </form>
          ) : (
            <>
              {isCalling ? (
                <IconWithBackground
                  className="h-16 w-16 flex-none cursor-pointer bg-red-500 hover:bg-white hover:transition-colors hover:duration-500 duration-500 text-white hover:text-red-500"
                  size="x-large"
                  icon={<PhoneIcon className="rotate-[0.375turn] h-8 w-8" />}
                  onClick={toggleConversation}
                />

              ) : (
                <IconWithBackground
                  className="h-16 w-16 flex-none cursor-pointer hover:bg-blue-600 hover:transition-colors hover:duration-500 duration-500 text-[#214eff] hover:text-white"
                  size="x-large"
                  icon={<PhoneIcon className="h-8 w-8" />}
                  onClick={toggleConversation}
                />
              )}
              <span className="w-112 whitespace-pre-wrap font-['Inter'] text-[20px] font-[400] leading-[28px] text-[#b3b3beff] text-center -tracking-[0.04em] mobile:h-auto mobile:w-full mobile:flex-none mobile:font-['Inter'] mobile:text-[18px] mobile:font-[400] mobile:leading-[24px] mobile:tracking-normal">
                {isCalling ? "Press to stop" : "Talk to Havana now"}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
